import * as React from 'react';
import { AccentDateUnbound } from '../unbound/AccentDateUnbound';
import { ConvertDateFromUTC, ConvertDateToUTC, t } from '../../services/HelperService';

const safeString = (v) => (v ?? '').toString();

/**
 * The viewer-controlled report date range template tokens. Shared contract with the
 * server: AdhocReportSessionProfile's brief rule teaches the AI to filter with these
 * tokens, and InsyteWebAPIService.TryGetDefaultTemplateTestValue supplies a "last 30
 * days" fallback so AI-authored queries test green before the picker ever runs.
 * The range is HALF-OPEN: dateFrom is the inclusive start instant, dateTo the exclusive
 * end (start of the day AFTER the last included day) — queries use "ge from and lt to".
 */
export const REPORT_DATE_FROM_TOKEN = '{{report.dateFrom}}';
export const REPORT_DATE_TO_TOKEN = '{{report.dateTo}}';

/**
 * Viewer-identity token ("my jobs" / "sales for the current user"): the SERVER resolves
 * it to the authenticated user's ID on every execution — the client never supplies (and
 * cannot override) its value, so shared artifacts show each viewer their own slice.
 * There is no UI for it: it must stay out of the assistant's template-value inputs.
 */
export const REPORT_CURRENT_USER_ID_TOKEN = '{{report.currentUserID}}';

/** True for tokens whose values the server owns — never collected from the user. */
export const isServerOwnedTemplateToken = (token) => (
  /^\{\{\s*report\.currentUserID\s*\}\}$/i.test(safeString(token))
);

const DATE_TOKEN_REGEX = /\{\{\s*report\.date(?:From|To)\s*\}\}/;

/** True when any of the session's queries references a report date-range token. */
export const usesReportDateRange = (queries) => (
  (Array.isArray(queries) ? queries : []).some((q) => DATE_TOKEN_REGEX.test(safeString(q?.query)))
);

/** Preset ids/labels; labels run through t() at render time. */
export const REPORT_DATE_RANGE_PRESETS = [
  { id: 'currentMonth', label: 'Current Month' },
  { id: 'mtd', label: 'Month to Date' },
  { id: 'lastMonth', label: 'Last Month' },
  { id: 'lastQuarter', label: 'Last Quarter' },
  { id: 'last6Months', label: 'Last 6 Months' },
  { id: 'ytd', label: 'Year to Date' },
  { id: 'lastYear', label: 'Last Year' },
  { id: 'custom', label: 'Custom' },
];

const startOfDay = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
};
const addDays = (d, n) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};

/**
 * Computes the half-open [from, to) UTC instants for a preset, TIMEZONE-AWARE: "today",
 * month/quarter/year boundaries are derived from the wall-clock date in the given IANA
 * timezone (the user's business unit), then converted back to UTC instants — so a
 * viewer in another timezone still gets the business unit's calendar, and DST
 * transitions come out right via the IANA round-trip.
 *
 * Returns { from, to, fromWall, toWall } (UTC instants plus the wall-clock boundary
 * dates for display), or null when the custom range is incomplete/invalid.
 */
export const computeReportDateRange = (presetId, timeZone, customFrom, customTo) => {
  const tz = safeString(timeZone) || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const today = startOfDay(ConvertDateFromUTC(new Date(), tz));
  const y = today.getFullYear();
  const m = today.getMonth();

  let fromWall;
  let toWall;
  switch (safeString(presetId)) {
    case 'mtd':
      fromWall = new Date(y, m, 1);
      toWall = addDays(today, 1);
      break;
    case 'lastMonth':
      fromWall = new Date(y, m - 1, 1);
      toWall = new Date(y, m, 1);
      break;
    case 'lastQuarter': {
      const quarterStartMonth = Math.floor(m / 3) * 3;
      fromWall = new Date(y, quarterStartMonth - 3, 1);
      toWall = new Date(y, quarterStartMonth, 1);
      break;
    }
    case 'last6Months':
      // Rolling: from the 1st of the month five months back through today.
      fromWall = new Date(y, m - 5, 1);
      toWall = addDays(today, 1);
      break;
    case 'ytd':
      fromWall = new Date(y, 0, 1);
      toWall = addDays(today, 1);
      break;
    case 'lastYear':
      fromWall = new Date(y - 1, 0, 1);
      toWall = new Date(y, 0, 1);
      break;
    case 'custom': {
      if (!customFrom || !customTo) return null;
      fromWall = startOfDay(customFrom);
      // The user's intuitive "to 30 Jun" includes 30 Jun: exclusive end is the next day.
      toWall = addDays(startOfDay(customTo), 1);
      break;
    }
    case 'currentMonth':
    default:
      fromWall = new Date(y, m, 1);
      toWall = new Date(y, m + 1, 1);
      break;
  }

  if (!(toWall > fromWall)) return null;
  return {
    from: ConvertDateToUTC(fromWall, tz),
    to: ConvertDateToUTC(toWall, tz),
    fromWall,
    toWall,
  };
};

/**
 * Human label for a computed range, e.g. "Last Month (1 Jun – 30 Jun 2026)" — the
 * inclusive display range formatted with the user's locale. Shown in the picker and
 * shipped to the report document as window.__reportEnv.dateRangeLabel so the report
 * can state which period it reflects.
 */
export const formatReportDateRangeLabel = (presetId, range, locale) => {
  const preset = REPORT_DATE_RANGE_PRESETS.find((p) => p.id === presetId);
  const name = t(preset?.label ?? 'Custom');
  if (!range) return name;
  try {
    const fmt = new Intl.DateTimeFormat(safeString(locale) || undefined, { day: 'numeric', month: 'short', year: 'numeric' });
    return `${name} (${fmt.format(range.fromWall)} – ${fmt.format(addDays(range.toWall, -1))})`;
  } catch {
    return name;
  }
};

/**
 * The report preview's date-range parameter bar: a preset selector (timezone-aware
 * convenience ranges) plus from/to pickers for Custom. Controlled: value is
 * { preset, customFrom, customTo }; onChange receives the next value — the host
 * computes the range, feeds the template values, and re-runs the queries.
 */
export const ReportDateRangePicker = ({ value, disabled, timeZone, locale, onChange }) => {
  const preset = safeString(value?.preset) || 'currentMonth';
  const isCustom = preset === 'custom';
  const range = computeReportDateRange(preset, timeZone, value?.customFrom, value?.customTo);
  const label = formatReportDateRangeLabel(preset, range, locale);

  return (
    <div data-tagname="reportDateRange" style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, fontWeight: 700 }}>{t('Date range')}</span>
      <select
        data-tagname="reportDateRangePreset"
        disabled={disabled}
        value={preset}
        onChange={(e) => onChange?.({ ...(value ?? {}), preset: e.target.value })}
        style={{ fontSize: 12, padding: '4px 6px', borderRadius: 4, border: '1px solid #ced4da' }}
      >
        {REPORT_DATE_RANGE_PRESETS.map((p) => (
          <option key={p.id} value={p.id}>{t(p.label)}</option>
        ))}
      </select>
      {isCustom && (
        <>
          <div style={{ width: 140 }}>
            <AccentDateUnbound
              dataTagName="reportDateRangeFrom"
              label={t('From')}
              disabled={disabled}
              defaultValue={value?.customFrom ?? null}
              onChange={(d) => onChange?.({ ...(value ?? {}), preset: 'custom', customFrom: d })}
            />
          </div>
          <div style={{ width: 140 }}>
            <AccentDateUnbound
              dataTagName="reportDateRangeTo"
              label={t('To')}
              disabled={disabled}
              defaultValue={value?.customTo ?? null}
              onChange={(d) => onChange?.({ ...(value ?? {}), preset: 'custom', customTo: d })}
            />
          </div>
        </>
      )}
      {!!range && (
        <span style={{ fontSize: 11, opacity: 0.65 }}>{label}</span>
      )}
      {isCustom && !range && (
        <span style={{ fontSize: 11, color: '#b71c1c' }}>{t('Pick both dates (from must not be after to).')}</span>
      )}
    </div>
  );
};
